<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Nullable } from '@nova/core';
import { PRESENTER_VALUE_FALLBACK } from '@satellite/components/elements/presenters/presenterValueFallback';
import { useCheckSpotPanel } from '@/composables/useCheckSpotPanel';
import { useCheckTaskPanel } from '@/composables/useCheckTaskPanel';
import useNotify from '@/composables/useNotify';
import type { SpotEventData } from '@/composables/useEventBasedDialog';
import { relatedObjectToYardSpot } from '@/helpers/checkTaskDetailsTitle';
import CheckSpotOccupiedView from '@/components/CheckSpotOccupiedView.vue';
import ElapsedTime from '@satellite/components/elements/ElapsedTime.vue';
import YardSpotStatusDialog from '@satellite/components/elements/YardSpotStatusDialog.vue';
import yardSpotService from '@satellite/services/yardSpot-service';

const { t } = useI18n();
const { notify } = useNotify();

const { dialogProps, handleAfterLeave, buildControls } = useCheckTaskPanel('open-check-spot-panel');

const spotId = computed((): Nullable<string> => dialogProps.value?.spotId ?? null);
const spot = computed(
  () => relatedObjectToYardSpot(spotId.value ? { id: spotId.value } : undefined) ?? null
);

const spotEventsOverride = ref<SpotEventData[] | null>(null);
const spotEvents = computed(
  () => spotEventsOverride.value ?? dialogProps.value?.spotEvents ?? null
);

watch(spotId, () => {
  spotEventsOverride.value = null;
});

async function refreshSpotEvents(): Promise<void> {
  if (!spotId.value) return;
  try {
    const events = (await yardSpotService.getEvents(spotId.value)) as SpotEventData[];
    spotEventsOverride.value = events ?? [];
  } catch {
    // ignore
  }
}

const {
  spotCode,
  spotStatus,
  areaName,
  currentAsset,
  hookedAsset,
  unhookedAsset,
  activeAssetVisit,
  occupiedView,
  reserveInfo,
  isLoading,
  isOccupied,
  isReserved,
  isOpenStatus,
  isClosedStatus,
  statusIcon,
  statusIconColor,
  durationCaptionKey,
  statusReferenceDateIso,
  reservedTaskLabel,
  showOccupiedAssetFallback,
  closeReason,
  closeObservation,
  onIntersect
} = useCheckSpotPanel({ spotId, spot, spotEvents });

const { isOpen, closePanel } = buildControls(onIntersect);

const statusDialogType = ref<'close' | 'open' | null>(null);
const isSubmitting = ref(false);

async function handleSpotStatusSubmit({
  reason,
  observation
}: {
  reason: string;
  observation: string;
}) {
  if (!spotId.value || isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    if (statusDialogType.value === 'close') {
      await yardSpotService.close(spotId.value, { reason, observation });
    } else {
      await yardSpotService.open(spotId.value, { reason: 'Opened from check spot panel' });
    }
    statusDialogType.value = null;
    await refreshSpotEvents();
  } catch {
    notify(t('checkAssetPanel.errors.updateFailed'), 'error');
  } finally {
    isSubmitting.value = false;
  }
}

const headerTitle = computed((): string => {
  if (areaName.value && spotCode.value) {
    return `${areaName.value} – ${spotCode.value}`;
  }

  return spotCode.value ?? PRESENTER_VALUE_FALLBACK;
});
</script>

<template>
  <v-dialog
    v-model="isOpen"
    data-testid="check-spot-panel"
    class="check-spot-panel-dialog"
    transition="slide-x-reverse-transition"
    scroll-strategy="none"
    @keydown.escape="closePanel"
    @after-leave="handleAfterLeave">
    <v-card
      class="check-spot-panel d-flex flex-column bg-surface rounded-0 border-s border-color-line-border"
      flat>
      <header
        class="check-spot-panel__header d-flex align-center justify-space-between flex-grow-0 px-4 py-3 border-b border-color-line-border">
        <div class="d-flex align-center gap-3 typography-heading-lg-bold text-truncate me-2">
          <v-icon
            size="28"
            class="flex-shrink-0 text-high-emphasis"
            icon="mdi-map-marker-outline" />
          <span class="text-truncate">
            {{ isLoading ? PRESENTER_VALUE_FALLBACK : headerTitle }}
          </span>
        </div>
        <v-btn
          icon
          variant="text"
          size="small"
          class="flex-shrink-0"
          :aria-label="t('global.basicTerms.close')"
          data-testid="check-spot-panel-dismiss-btn"
          @click="closePanel">
          <v-icon icon="mdi-close" />
        </v-btn>
      </header>

      <div class="flex-grow-1 overflow-y-auto d-flex flex-column pa-4 gap-4 bg--color-neutral-10">
        <v-card
          v-if="!isLoading"
          variant="flat"
          border
          class="flex-shrink-0"
          data-testid="check-spot-panel-status-row">
          <v-card-text class="d-flex gap-4">
            <div class="flex-1-1-0 d-flex flex-column gap-1 min-width-0">
              <span class="typography-body-sm-bold">
                {{ t('checkSpotPanel.header.substatus') }}
              </span>
              <div class="d-flex align-center gap-1">
                <v-icon size="16" :icon="statusIcon" :color="statusIconColor" />
                <span class="typography-body-sm text-medium-emphasis text-capitalize">
                  {{ t(`checkSpotPanel.status.${spotStatus}`) }}
                </span>
              </div>
            </div>
            <v-divider vertical />
            <div class="flex-1-1-0 d-flex flex-column gap-1 min-width-0">
              <span v-if="durationCaptionKey" class="typography-body-sm-bold">
                {{ t(durationCaptionKey) }}
              </span>
              <ElapsedTime
                v-if="statusReferenceDateIso"
                class="typography-body-sm text-medium-emphasis"
                :from="statusReferenceDateIso"
                variant="human"
                :update-interval="60_000" />
              <span v-else class="typography-body-sm text-medium-emphasis">—</span>
            </div>
          </v-card-text>
        </v-card>
        <v-card v-else variant="flat" border>
          <v-card-text>
            <v-skeleton-loader type="sentences" class="bg-transparent" />
          </v-card-text>
        </v-card>

        <template v-if="isLoading">
          <v-card variant="flat" border class="flex-grow-1">
            <v-card-text>
              <v-skeleton-loader type="article" class="bg-transparent" />
            </v-card-text>
          </v-card>
        </template>

        <check-spot-occupied-view
          v-else-if="isOccupied"
          :occupied-view="occupiedView"
          :hooked-asset="hookedAsset"
          :unhooked-asset="unhookedAsset"
          :asset-visit="activeAssetVisit"
          :current-asset="currentAsset"
          :show-current-asset="showOccupiedAssetFallback" />

        <template v-else-if="isReserved && reserveInfo">
          <div class="d-flex flex-column gap-3">
            <v-card variant="flat" border>
              <v-card-text class="d-flex flex-column gap-1">
                <span class="typography-body-sm-bold">
                  {{ t('checkSpotPanel.fields.reservedFor') }}
                </span>
                <span class="typography-body-sm text-medium-emphasis text-capitalize">
                  {{
                    t(
                      `checkSpotPanel.reserveEntity.${reserveInfo.entityName}`,
                      reserveInfo.entityName
                    )
                  }}
                </span>
              </v-card-text>
            </v-card>
            <v-card v-if="reservedTaskLabel" variant="flat" border>
              <v-card-text class="d-flex flex-column gap-1">
                <span class="typography-body-sm-bold">
                  {{ t('checkSpotPanel.fields.taskNumber') }}
                </span>
                <span class="typography-body-sm text-medium-emphasis">{{ reservedTaskLabel }}</span>
              </v-card-text>
            </v-card>
            <v-card variant="flat" border>
              <v-card-text class="d-flex flex-column gap-1">
                <div class="d-flex align-center justify-space-between">
                  <span class="typography-body-sm-bold">
                    {{ t('checkSpotPanel.fields.additionalNotes') }}
                  </span>
                  <v-icon size="16" icon="mdi-pencil-outline" />
                </div>
                <span class="typography-body-sm text-medium-emphasis">
                  {{ t('checkSpotPanel.reserveNotes') }}
                </span>
              </v-card-text>
            </v-card>
          </div>
        </template>

        <template v-else-if="isOpenStatus">
          <div class="flex-grow-1 d-flex align-center justify-center">
            <span class="typography-body-sm text-medium-emphasis text-center">
              {{ t('checkSpotPanel.openMessage') }}
            </span>
          </div>
        </template>

        <template v-else-if="isClosedStatus">
          <div class="d-flex flex-column gap-3">
            <v-card variant="flat" border data-testid="check-spot-panel-close-reason">
              <v-card-text class="d-flex flex-column gap-1">
                <span class="typography-body-sm-bold">
                  {{ t('checkSpotPanel.closed.reason') }}
                </span>
                <span class="typography-body-sm text-medium-emphasis">
                  {{
                    closeReason
                      ? t(`checkSpotPanel.closed.reasons.${closeReason}`, closeReason)
                      : PRESENTER_VALUE_FALLBACK
                  }}
                </span>
              </v-card-text>
            </v-card>
            <v-card variant="flat" border data-testid="check-spot-panel-close-observation">
              <v-card-text class="d-flex flex-column gap-1">
                <span class="typography-body-sm-bold">
                  {{ t('checkSpotPanel.closed.observation') }}
                </span>
                <span class="typography-body-sm text-medium-emphasis">
                  {{ closeObservation ?? PRESENTER_VALUE_FALLBACK }}
                </span>
              </v-card-text>
            </v-card>
          </div>
        </template>

        <template v-else>
          <div class="flex-grow-1 d-flex align-center justify-center">
            <span class="typography-body-sm text-medium-emphasis">
              {{ t(`checkSpotPanel.status.${spotStatus}`) }}
            </span>
          </div>
        </template>
      </div>

      <footer v-if="isOpenStatus || isClosedStatus" class="flex-grow-0 pa-4 pt-0">
        <v-btn
          v-if="isOpenStatus"
          block
          size="large"
          variant="outlined"
          class="typography-button-lg"
          prepend-icon="mdi-cancel"
          data-testid="check-spot-panel-close-btn"
          @click="statusDialogType = 'close'">
          {{ t('checkSpotPanel.closeSpot') }}
        </v-btn>
        <v-btn
          v-else-if="isClosedStatus"
          block
          size="large"
          color="primary"
          class="typography-button-lg"
          prepend-icon="mdi-check"
          data-testid="check-spot-panel-open-btn"
          @click="statusDialogType = 'open'">
          {{ t('checkSpotPanel.openSpot') }}
        </v-btn>
      </footer>
    </v-card>

    <yard-spot-status-dialog
      :status-type="statusDialogType"
      @submit="handleSpotStatusSubmit"
      @close="statusDialogType = null" />
  </v-dialog>
</template>

<style scoped lang="scss">
@use '@/styles/partials/side-panel' as *;

.check-spot-panel {
  @include side-panel-card;
}

.check-spot-panel-dialog :deep(.v-overlay__content) {
  @include side-panel-dialog-overlay;
}

.check-spot-panel__header {
  @include side-panel-header;
}

.min-width-0 {
  min-inline-size: 0;
}
</style>
